export const LEAVER_PENALTY_APPEAL_KIND = "leaver_penalty";
export const LEAVER_PENALTY_APPEAL_CATEGORY = "reputation_appeal";

function normalizeOptionalString(value) {
  if (value == null) {
    return null;
  }

  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
}

function resolveMatchId(matchId, match) {
  return (
    normalizeOptionalString(matchId) ||
    normalizeOptionalString(match?.matchId) ||
    normalizeOptionalString(match?._id)
  );
}

function resolveMatchUrl(matchId, matchUrl) {
  const normalizedMatchUrl = normalizeOptionalString(matchUrl);
  if (normalizedMatchUrl) {
    return normalizedMatchUrl;
  }

  const normalizedMatchId = normalizeOptionalString(matchId);
  return normalizedMatchId ? `/match/${normalizedMatchId}` : null;
}

export function buildLeaverPenaltyAppealDescription({ matchId, matchUrl }) {
  return [
    "I am appealing the leaver penalty applied for this match:",
    "",
    `Match: ${matchUrl}`,
    `Match ID: ${matchId}`,
    "Penalty type: leaver_penalty",
    "",
    "Reason for appeal:",
    "",
    "Additional context:",
  ].join("\n");
}

export function buildLeaverPenaltyAppealPrefill({
  matchId,
  matchUrl,
  reputationHistoryId = null,
} = {}) {
  const resolvedMatchId = normalizeOptionalString(matchId);
  const resolvedMatchUrl = resolveMatchUrl(resolvedMatchId, matchUrl);
  const normalizedReputationHistoryId = normalizeOptionalString(
    reputationHistoryId
  );

  if (!resolvedMatchId || !resolvedMatchUrl || !normalizedReputationHistoryId) {
    return null;
  }

  const appeal = {
    kind: LEAVER_PENALTY_APPEAL_KIND,
    matchId: resolvedMatchId,
    matchUrl: resolvedMatchUrl,
    reputationHistoryId: normalizedReputationHistoryId,
  };

  return {
    category: LEAVER_PENALTY_APPEAL_CATEGORY,
    subject: `Appeal leaver penalty for match ${resolvedMatchId}`,
    description: buildLeaverPenaltyAppealDescription({
      matchId: resolvedMatchId,
      matchUrl: resolvedMatchUrl,
    }),
    appeal,
  };
}

export function buildLeaverPenaltyAppealUrl(options = {}) {
  const prefill = buildLeaverPenaltyAppealPrefill(options);
  if (!prefill) {
    return "/support";
  }

  const params = new URLSearchParams({
    category: prefill.category,
    appealKind: prefill.appeal.kind,
    matchId: prefill.appeal.matchId,
    matchUrl: prefill.appeal.matchUrl,
  });

  if (prefill.appeal.reputationHistoryId) {
    params.set(
      "reputationHistoryId",
      prefill.appeal.reputationHistoryId
    );
  }

  return `/support?${params.toString()}`;
}

export function getLeaverPenaltyAppealPrefillFromLocation({
  search = "",
  state = null,
} = {}) {
  const statePrefill = state?.supportTicketPrefill || state?.appealPrefill || null;
  if (statePrefill?.appeal?.kind === LEAVER_PENALTY_APPEAL_KIND) {
    return buildLeaverPenaltyAppealPrefill({
      matchId: statePrefill.appeal.matchId,
      matchUrl: statePrefill.appeal.matchUrl,
      reputationHistoryId: statePrefill.appeal.reputationHistoryId,
    });
  }

  const params = new URLSearchParams(
    String(search || "").startsWith("?") ? String(search).slice(1) : String(search)
  );

  if (params.get("appealKind") !== LEAVER_PENALTY_APPEAL_KIND) {
    return null;
  }

  return buildLeaverPenaltyAppealPrefill({
    matchId: params.get("matchId"),
    matchUrl: params.get("matchUrl"),
    reputationHistoryId: params.get("reputationHistoryId"),
  });
}

export function getSupportTicketInitialState({
  userEmail = "",
  search = "",
  state = null,
} = {}) {
  const prefill = getLeaverPenaltyAppealPrefillFromLocation({ search, state });

  return {
    form: {
      category: prefill?.category || "other",
      email: normalizeOptionalString(userEmail) || "",
      subject: prefill?.subject || "",
      description: prefill?.description || "",
    },
    appeal: prefill?.appeal || null,
  };
}

export function getViewerLeaverPenaltyAppealContext({
  matchId,
  match,
  viewer,
} = {}) {
  const viewerSteamIds = new Set(
    [viewer?.steamId, viewer?.steamId64, viewer?.steam64Id]
      .map(normalizeOptionalString)
      .filter(Boolean)
  );

  if (viewerSteamIds.size === 0) {
    return null;
  }

  const leaver = (Array.isArray(match?.leavers) ? match.leavers : []).find((entry) =>
    viewerSteamIds.has(normalizeOptionalString(entry?.steamId64))
  );
  if (
    !leaver ||
    normalizeOptionalString(leaver?.status) !== "reputation_applied" ||
    !normalizeOptionalString(leaver?.reputationHistoryId)
  ) {
    return null;
  }

  const resolvedMatchId = resolveMatchId(matchId, match);
  const resolvedMatchUrl = resolveMatchUrl(resolvedMatchId, null);
  if (!resolvedMatchId || !resolvedMatchUrl) {
    return null;
  }

  return {
    leaver,
    matchId: resolvedMatchId,
    matchUrl: resolvedMatchUrl,
    appealUrl: buildLeaverPenaltyAppealUrl({
      matchId: resolvedMatchId,
      matchUrl: resolvedMatchUrl,
      reputationHistoryId: leaver?.reputationHistoryId || null,
    }),
  };
}
